import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PushPrompt from "@/components/PushPrompt";
import { Users, ClipboardList, FileSearch, AlertTriangle, Syringe, CalendarDays, FileText } from "lucide-react";

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unseenAppts, setUnseenAppts] = useState(0);
  const [unseenMetabolic, setUnseenMetabolic] = useState({ count: 0, latest: [] });
  const [unseenAnamnesis, setUnseenAnamnesis] = useState({ count: 0, latest: [] });

  useEffect(() => {
    (async () => {
      try {
        const [s, a, u, m, an] = await Promise.all([
          api.get("/admin/dashboard-stats"),
          api.get("/admin/alerts"),
          api.get("/admin/appointments/unseen-count"),
          api.get("/admin/metabolic-tests/unseen-count"),
          api.get("/admin/anamnesis/unseen-count"),
        ]);
        setStats(s.data);
        setAlerts(a.data);
        setUnseenAppts(u.data.count || 0);
        setUnseenMetabolic(m.data || { count: 0, latest: [] });
        setUnseenAnamnesis(an.data || { count: 0, latest: [] });
      } catch (_) { /* silent */ }
    })();
  }, []);

  const cards = [
    { k: "total_patients", label: "Pacientes ativos", icon: Users },
    { k: "active_protocols", label: "Protocolos em andamento", icon: ClipboardList },
    { k: "pending_exams", label: "Exames pendentes", icon: FileSearch },
    { k: "upcoming_applications", label: "Aplicações agendadas", icon: Syringe },
    { k: "critical_patients", label: "Pacientes críticos", icon: AlertTriangle },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard-page">
      <div>
        <div className="overline text-accent mb-3">Painel clínico</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Visão geral</h1>
      </div>

      <PushPrompt />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map(({ k, label, icon: Icon }) => (
          <div key={k} className="bg-card border border-border rounded-sm p-5" data-testid={`admin-stat-${k}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="overline text-muted-foreground">{label}</span>
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div className="font-heading text-3xl font-bold">{stats?.[k] ?? "—"}</div>
          </div>
        ))}
      </div>

      {unseenAppts > 0 && (
        <Link
          to="/admin/agendamentos"
          className="block bg-gold/10 border-2 border-gold rounded-sm p-5 hover:bg-gold/20 transition-colors"
          data-testid="admin-new-appointments-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-gold text-primary grid place-items-center flex-none">
              <CalendarDays className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="overline text-gold mb-1">Novos agendamentos</div>
              <div className="font-heading font-semibold text-primary">
                {unseenAppts} agendamento{unseenAppts > 1 ? "s" : ""} novo{unseenAppts > 1 ? "s" : ""} aguardando confirmação
              </div>
            </div>
            <span className="text-primary text-sm font-semibold">Ver agora →</span>
          </div>
        </Link>
      )}

      {unseenMetabolic.count > 0 && (
        <Link
          to="/admin/testes-metabolicos"
          className="block bg-emerald-500/10 border-2 border-emerald-500 rounded-sm p-5 hover:bg-emerald-500/20 transition-colors"
          data-testid="admin-new-metabolic-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-emerald-500 text-white grid place-items-center flex-none">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="overline text-emerald-700 mb-1">Testes metabólicos respondidos</div>
              <div className="font-heading font-semibold text-primary">
                {unseenMetabolic.count} teste{unseenMetabolic.count > 1 ? "s" : ""} novo{unseenMetabolic.count > 1 ? "s" : ""} aguardando sua leitura
              </div>
              {unseenMetabolic.latest && unseenMetabolic.latest.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  Último: {unseenMetabolic.latest[0].patient_name || "Paciente"}
                </div>
              )}
            </div>
            <span className="text-primary text-sm font-semibold">Revisar →</span>
          </div>
        </Link>
      )}

      {unseenAnamnesis.count > 0 && (
        <Link
          to="/admin/anamnese"
          className="block bg-sky-500/10 border-2 border-sky-500 rounded-sm p-5 hover:bg-sky-500/20 transition-colors"
          data-testid="admin-new-anamnesis-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-sky-500 text-white grid place-items-center flex-none">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="overline text-sky-700 mb-1">Fichas de anamnese respondidas</div>
              <div className="font-heading font-semibold text-primary">
                {unseenAnamnesis.count} ficha{unseenAnamnesis.count > 1 ? "s" : ""} nova{unseenAnamnesis.count > 1 ? "s" : ""} aguardando sua leitura
              </div>
              {unseenAnamnesis.latest && unseenAnamnesis.latest.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">
                  Última: {unseenAnamnesis.latest[0].patient_name || "Paciente"}
                </div>
              )}
            </div>
            <span className="text-primary text-sm font-semibold">Revisar →</span>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-sm p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-muted-foreground">Alertas automáticos</div>
              <h3 className="font-heading text-lg font-semibold">Intervenções recomendadas</h3>
            </div>
            <Link to="/admin/alertas" className="text-sm text-primary font-semibold hover:underline" data-testid="view-all-alerts-link">Ver todos</Link>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nenhum alerta no momento.</div>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 6).map((a) => (
                <Link
                  key={a.id}
                  to={`/admin/pacientes/${a.patient_id}`}
                  className="flex items-center gap-3 border border-border rounded-sm px-4 py-3 hover:bg-muted transition-colors"
                  data-testid={`alert-row-${a.id}`}
                >
                  <AlertTriangle className={`w-4 h-4 flex-none ${a.severity === "high" ? "text-destructive" : "text-warning"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground">{a.patient_name}</div>
                  </div>
                  <span className="overline text-muted-foreground">{a.severity}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-primary text-primary-foreground rounded-sm p-6">
          <div className="overline opacity-80 mb-2">Acesso rápido</div>
          <h3 className="font-heading text-xl font-semibold mb-6">Seus atalhos</h3>
          <div className="space-y-2 text-sm">
            <Link to="/admin/pacientes" className="block px-4 py-3 rounded-sm bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors" data-testid="admin-quick-patients">
              → Lista completa de pacientes
            </Link>
            <Link to="/admin/exames?status=pending_review" className="block px-4 py-3 rounded-sm bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors" data-testid="admin-quick-exams">
              → Exames aguardando análise
            </Link>
            <Link to="/admin/alertas" className="block px-4 py-3 rounded-sm bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors" data-testid="admin-quick-alerts">
              → Fila de alertas críticos
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
